import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Inbox, PlusSquare, Settings, LogOut, Bell } from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { isWingmanSupported, registerWingmanServiceWorker, enableWingmanPush } from './wingmanPush';
import { toast } from 'sonner';
import { devError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Mobile-first shell for the supplier wingman PWA. */
export default function WingmanLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [pushPrompted, setPushPrompted] = useState(false);

  useEffect(() => {
    // Inject the wingman manifest tag (so this page is installable as PWA).
    let link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute('href');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.setAttribute('href', '/manifest-wingman.json');

    // Theme color tag for the Android status bar.
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    const originalTheme = meta.getAttribute('content');
    meta.setAttribute('content', '#0F172A');

    registerWingmanServiceWorker();

    return () => {
      if (original) link.setAttribute('href', original); else link.remove();
      if (originalTheme) meta.setAttribute('content', originalTheme);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/wingman/me`);
        if (!cancelled) setMe(data);
      } catch (e) {
        if (!cancelled) devError('wingman/me failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const promptPush = async () => {
    setPushPrompted(true);
    if (!isWingmanSupported()) {
      toast.info('Push notifications need Chrome/Edge/Firefox on Android.');
      return;
    }
    const { ok, reason } = await enableWingmanPush();
    if (ok) toast.success('Push notifications enabled');
    else if (reason === 'permission_denied') toast.error('You blocked notifications. Enable them in browser settings.');
    else toast.error(`Could not enable push (${reason})`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0F172A] text-white flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold text-[#D4AF37] mb-2">PatinaMart Wingman</h1>
        <p className="text-sm text-slate-400 mb-6">Pair this phone with your supplier dashboard to get started.</p>
        <button
          onClick={() => navigate('/wingman/pair')}
          className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-semibold"
          data-testid="wingman-go-pair"
        >
          Pair my phone
        </button>
        <p className="text-xs text-slate-500 mt-6">Already have a desktop login? <button onClick={() => navigate('/auth?from=wingman')} className="underline">Sign in instead</button></p>
      </div>
    );
  }

  if (user.user_type !== 'supplier') {
    return (
      <div className="min-h-screen bg-[#0F172A] text-white flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-xl font-bold text-[#D4AF37] mb-2">Wingman is for suppliers</h1>
        <p className="text-sm text-slate-400">Your account is a buyer account. Open PatinaMart in your browser instead.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex flex-col" data-testid="wingman-layout">
      <header className="px-5 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0F172A]/95 backdrop-blur z-10">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/80">Wingman</p>
          <h1 className="text-lg font-semibold truncate">{me?.company_name || me?.name || 'Supplier'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!pushPrompted && (
            <button onClick={promptPush} className="p-2 rounded-full bg-white/5 hover:bg-white/10" title="Enable notifications" data-testid="wingman-enable-push">
              <Bell className="w-4 h-4 text-[#D4AF37]" />
            </button>
          )}
          <button onClick={logout} className="p-2 rounded-full bg-white/5 hover:bg-white/10" title="Sign out" data-testid="wingman-logout">
            <LogOut className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet context={{ me, refreshMe: () => axios.get(`${API}/wingman/me`).then((r) => setMe(r.data)).catch(() => {}) }} />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-[#0B1224]/95 backdrop-blur border-t border-white/5 grid grid-cols-3 z-20" data-testid="wingman-tabbar">
        <TabLink to="/wingman/inbox" icon={Inbox} label="Inbox" badge={me?.pending_count} />
        <TabLink to="/wingman/quick-add" icon={PlusSquare} label="Quick add" />
        <TabLink to="/wingman/settings" icon={Settings} label="Settings" />
      </nav>
    </div>
  );
}

function TabLink({ to, icon: Icon, label, badge }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center py-2.5 text-[11px] gap-0.5 ${isActive ? 'text-[#D4AF37]' : 'text-slate-400'}`
      }
      data-testid={`wingman-tab-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="relative">
        <Icon className="w-5 h-5" />
        {!!badge && badge > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] rounded-full bg-emerald-500 text-[10px] font-bold text-white flex items-center justify-center px-1">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span>{label}</span>
    </NavLink>
  );
}
